/**
 * Authoring helpers. Level files are plain data, but raw prefab objects are
 * verbose and easy to get wrong, so everything here takes the measurement a
 * designer actually thinks in: "floor top at y", "wall base at y, h tall".
 */

/** Floor or ceiling slab. `y` is the TOP surface. */
export const slab = (x, y, z, w, d, m = 'plate', t = 1.2, extra = {}) =>
  ({ t: 'box', p: [x, y - t / 2, z], s: [w, t, d], m, ...extra });

/** Slab whose `y` is the BOTTOM surface (ceilings, overhangs). */
export const roof = (x, y, z, w, d, m = 'plate', t = 1.2, extra = {}) =>
  ({ t: 'box', p: [x, y + t / 2, z], s: [w, t, d], m, ...extra });

/** Wall with its faces pointing along X. `y` is the base, `h` the height. */
export const wallX = (x, y, z, d, h, m = 'panel', t = 1.2, extra = {}) =>
  ({ t: 'box', p: [x, y + h / 2, z], s: [t, h, d], m, ...extra });

/** Wall with its faces pointing along Z. */
export const wallZ = (x, y, z, w, h, m = 'panel', t = 1.2, extra = {}) =>
  ({ t: 'box', p: [x, y + h / 2, z], s: [w, h, t], m, ...extra });

export const block = (x, y, z, w, h, d, m = 'plate', extra = {}) =>
  ({ t: 'box', p: [x, y + h / 2, z], s: [w, h, d], m, ...extra });

export const pillar = (x, y, z, w, h, m = 'trim') => block(x, y, z, w, h, w, m);

/** A closed rectangular room: floor, four walls, optional roof. */
export function room({
  x = 0, y = 0, z = 0, w = 20, d = 20, h = 8,
  floor = 'plate', walls = 'plate', ceiling = null,
  portalWalls = [], thickness = 1.2,
} = {}) {
  const out = [slab(x, y, z, w, d, floor, thickness)];
  const pick = (side) => (portalWalls.includes(side) ? 'panel' : walls);
  out.push(wallZ(x, y, z - d / 2 - thickness / 2, w + thickness * 2, h, pick('n'), thickness));
  out.push(wallZ(x, y, z + d / 2 + thickness / 2, w + thickness * 2, h, pick('s'), thickness));
  out.push(wallX(x - w / 2 - thickness / 2, y, z, d, h, pick('w'), thickness));
  out.push(wallX(x + w / 2 + thickness / 2, y, z, d, h, pick('e'), thickness));
  if (ceiling) out.push(roof(x, y + h, z, w, d, ceiling, thickness));
  return out;
}

/** A vertical shaft: four walls from `yBottom` to `yTop`, optional floor. */
export function shaft({
  x = 0, z = 0, w = 20, d = 20, yBottom = -60, yTop = 0,
  walls = 'plate', floor = null, thickness = 1.5,
} = {}) {
  const h = yTop - yBottom;
  const out = [
    wallZ(x, yBottom, z - d / 2 - thickness / 2, w + thickness * 2, h, walls, thickness),
    wallZ(x, yBottom, z + d / 2 + thickness / 2, w + thickness * 2, h, walls, thickness),
    wallX(x - w / 2 - thickness / 2, yBottom, z, d, h, walls, thickness),
    wallX(x + w / 2 + thickness / 2, yBottom, z, d, h, walls, thickness),
  ];
  if (floor) out.push(slab(x, yBottom, z, w, d, floor, thickness));
  return out;
}

export const goal = (x, y, z, yaw = 0, extra = {}) => ({ t: 'goal', p: [x, y, z], yaw, ...extra });
export const shard = (x, y, z) => ({ t: 'shard', p: [x, y, z] });
export const checkpoint = (x, y, z, yaw = null) => ({ t: 'checkpoint', p: [x, y, z], yaw });
export const pad = (x, y, z, power = 21, dir = [0, 1, 0]) => ({ t: 'pad', p: [x, y, z], power, dir });
export const lift = (x, y, z, h = 20, r = 2.2) => ({ t: 'lift', p: [x, y, z], h, r });
export const fizzler = (x, y, z, w = 4, h = 5, yaw = 0) => ({ t: 'fizzler', p: [x, y, z], w, h, yaw });
export const speedgate = (x, y, z, w, h, min, yaw = 0) => ({ t: 'speedgate', p: [x, y, z], w, h, min, yaw });
export const ring = (x, y, z, dir, power = 14, r = 2.6) => ({ t: 'ring', p: [x, y, z], dir, power, r });
export const bouncefloor = (x, y, z, w, d, rest = 0.94) => ({ t: 'bouncefloor', p: [x, y, z], s: [w, d], rest });
export const gravfield = (x, y, z, w, h, d, scale = 0.28) => ({ t: 'gravfield', p: [x, y, z], s: [w, h, d], scale });
export const wind = (x, y, z, w, h, d, dir, accel = 18, max = 26) => ({ t: 'wind', p: [x, y, z], s: [w, h, d], dir, accel, max });
export const hazard = (x, y, z, w, d, t = 0.5) => ({ t: 'hazard', p: [x, y, z], s: [w, t, d] });
export const sign = (x, y, z, yaw, lines, accent, h) => ({ t: 'sign', p: [x, y, z], yaw, lines, accent, h });
export const shatter = (x, y, z, s, min) => ({ t: 'shatter', p: [x, y, z], s, min });
export const beam = (x, y, z, len = 12, yaw = 0, spin = 0, shock = 2) => ({ t: 'beam', p: [x, y, z], len, yaw, spin, shock });
export const boost = (x, y, z, w, d, dir, accel = 26, max = 34) => ({ t: 'boost', p: [x, y, z], s: [w, d], dir, accel, max });
export const ice = (x, y, z, w, d) => ({ t: 'ice', p: [x, y, z], s: [w, d] });
export const acid = (x, y, z, w, d) => ({ t: 'acid', p: [x, y, z], s: [w, d] });
export const crusher = (x, y, z, to, s, period = 3, phase = 0) => ({ t: 'crusher', p: [x, y, z], to, s, period, phase });
export const sentinel = (x, y, z, range = 40, lock = 1.2, shock = 3) => ({ t: 'sentinel', p: [x, y, z], range, lock, shock });
export const neon = (x, y, z, s, color = 'cyan', opts = {}) => ({ t: 'strip', p: [x, y, z], s, color, ...opts });
export const deco = (x, y, z, s, m = 'trim', r = null) => ({ t: 'deco', p: [x, y, z], s, m, r });
export const mover = (x, y, z, to, s, period = 6, extra = {}) =>
  ({ t: 'mover', p: [x, y, z], to, s, period, ...extra });

/** Ring of neon strips around a rectangular platform edge. */
export function edgeLights(x, y, z, w, d, color = 'cyan') {
  return [
    neon(x, y + 0.06, z - d / 2, [w, 0.1, 0.1], color),
    neon(x, y + 0.06, z + d / 2, [w, 0.1, 0.1], color),
    neon(x - w / 2, y + 0.06, z, [0.1, 0.1, d], color),
    neon(x + w / 2, y + 0.06, z, [0.1, 0.1, d], color),
  ];
}

export const emitter = (x, y, z, dir, range = 120, shock = 2) => ({ t: 'emitter', p: [x, y, z], dir, range, shock });
export const receptacle = (x, y, z, id, r = 0.9) => ({ t: 'receptacle', p: [x, y, z], id, r });
export const gate = (x, y, z, link, w = 6, h = 8, yaw = 0, grace = 2) => ({ t: 'gate', p: [x, y, z], link, w, h, yaw, grace });
